import { catalog } from '../core/catalog.js';
import { homeFilter } from '../core/home-filters.js';
import { stripCountry } from '../core/country.js';
import { viewers } from '../core/viewers.js';
import { avatarHtml } from './who.js';
import { epg } from '../core/epg.js';
import { t } from '../core/i18n.js';
import { escapeHtml, debounce, formatMinutes, formatDate } from '../core/utils.js';
import { device } from '../core/device.js';
import { storage, settings } from '../core/storage.js';
import { i18n } from '../core/i18n.js';
import { notify } from '../core/notify.js';
import { promoSlides, promoHtml } from './home-promo.js';
import { cachedSlides, refreshSlides } from '../core/hero-slides.js';
import { pickNewcomers } from '../core/hero-news.js';
import { movieCard as plainMovieCard, seriesCard as plainSeriesCard, channelCard, continueCard, collectionCard, row, topItem, skeletonRow, enhance, empty, imageUrl, firstGenre, bindExpandCards } from '../ui/render.js';

/* Las tarjetas de las filas de Películas y Series del Inicio siguen el mismo ajuste «Tarjeta ampliada» que esas pantallas: 'row' = la
   tarjeta crece dentro de la propia fila; 'float' = sale flotando encima (ventana). */
const expandNow = () => settings.get('peekCard') !== false && settings.get('peekStyle') !== 'float' && (device.hasHover || device.isTV || document.documentElement.classList.contains('mode-tv'));
const movieCard = (item, opts = {}) => plainMovieCard(item, Object.assign({ expand: expandNow() }, opts));
const seriesCard = (item, opts = {}) => plainSeriesCard(item, Object.assign({ expand: expandNow() }, opts));

const FEATURED_COUNT = 5;
/** Ajuste «Cambio automático de la cabecera»: milisegundos entre destacados, 0 si no cambia solo. */
export function heroIntervalMs() {
    const v = Number(settings.get('heroInterval'));
    return isFinite(v) && v >= 0 ? v * 1000 : 8000;
}
const RECOMMENDED_MIN = 4;
const TRENDING_MIN = 4;
const TOP_MIN = 3; // con menos títulos que esto, la fila del Top 10 no sale

/** Varios destacados para el carrusel: bien valorados y recientes, con fondo si lo hay, y algo de azar. */
function pickFeaturedList(n = FEATURED_COUNT) {
    const hf = homeFilter();
    const pool = hf.list('vod', catalog.visible('vod')).filter((m) => m.poster).slice(0, 400);
    const series = hf.list('series', catalog.visible('series')).filter((s) => s.backdrop || s.poster).slice(0, 200);
    const all = pool.map((m) => ({ kind: 'vod', item: m })).concat(series.map((s) => ({ kind: 'series', item: s })));
    if (!all.length) return [];
    const score = (x) => (x.item.rating || 0) + (x.item.added ? 1 : 0) + (x.item.backdrop ? 2 : 0);
    // Novedades del catálogo (solo con el catálogo al día): entran repartidas entre los destacados, sin abrir el carrusel.
    const news = pickNewcomers(all, { updatedAt: catalog.updatedAt });
    const isNews = new Set(news.map((x) => x.kind + ':' + x.item.id));
    const top = all.filter((x) => !isNews.has(x.kind + ':' + x.item.id)).sort((a, b) => score(b) - score(a)).slice(0, 30);
    for (let i = top.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [top[i], top[j]] = [top[j], top[i]];
    }
    if (!news.length) return top.slice(0, Math.min(n, top.length));
    return interleavePromos(top.slice(0, Math.max(1, n - news.length)), news).slice(0, n);
}

/** Datos del destacado: los de la lista del proveedor completados con la ficha si ya se conoce. */
export function heroData(featured) {
    const it = featured.item;
    const info = featured.info || catalog.peekInfo(featured.kind, it.id) || {};
    const secs = info.durationSecs || 0;
    return {
        name: it.name,
        image: info.backdrop || it.backdrop || it.poster || info.poster || '',
        fallback: it.poster || info.poster || '',
        rating: Number(it.rating || info.rating) || 0,
        year: it.year || info.year || '',
        genre: firstGenre(it.genre || info.genre),
        duration: secs ? formatMinutes(secs) : (Number(info.episodeRunTime) > 0 ? info.episodeRunTime + ' ' + t('common.minutes') : ''),
        plot: it.plot || info.plot || ''
    };
}

export function heroMetaHtml(d) {
    const parts = [];
    if (d.rating) parts.push(`<span class="hero-rating"><span class="mi">star</span>${d.rating.toFixed(1)}</span>`);
    [d.year, d.genre, d.duration].filter(Boolean).forEach((m) => parts.push(`<span>${escapeHtml(m)}</span>`));
    return parts.join('<span class="hero-sep" aria-hidden="true">·</span>');
}

/** ¿Hay películas o series con carátula para destacar? Si no, la cabecera cuenta qué es Filoteca. */
function hasFeaturable() {
    return catalog.visible('vod').some((m) => m.poster) || catalog.visible('series').some((x) => x.backdrop || x.poster);
}

/** Pantallas de presentación (solo canales): los logos de los canales del usuario van en el mosaico. */
function pickPromoList() {
    const logos = catalog.visible('live').filter((c) => c.logo).slice(0, 12).map((c) => c.logo);
    const acc = window.IPTV && window.IPTV.account;
    return promoSlides({ logos: logos.sort(() => Math.random() - 0.5), remote: cachedSlides(), canRecord: !!(acc && acc.canRecord) });
}

/**
 * Mezcla las pantallas de presentación/publicidad con los destacados: se reparten a intervalos regulares y nunca abren el
 * carrusel (la primera sigue siendo una película o serie).
 */
export function interleavePromos(real, promos) {
    if (!promos.length) return real;
    if (!real.length) return promos;
    const out = [];
    const n = real.length;
    const at = promos.map((_, j) => Math.max(1, Math.floor(((j + 1) * n) / (promos.length + 1))));
    real.forEach((r, i) => {
        out.push(r);
        promos.forEach((p, j) => { if (at[j] === i + 1) out.push(p); });
    });
    // Si hubiera más pantallas que huecos distintos, las que no entraron van al final
    promos.forEach((p, j) => { if (at[j] > n) out.push(p); });
    return out;
}

/** Lo que enseña la cabecera: destacados + pantallas «siempre»; sin destacados, todas las pantallas. */
function buildHeroList() {
    const real = pickFeaturedList();
    const promos = pickPromoList();
    return real.length ? interleavePromos(real, promos.filter((s) => s.show === 'always')) : promos;
}

export function heroHtml(list, index, { navDefault = true, lib = false } = {}) {
    const featured = list[index];
    if (featured && featured.kind === 'promo') return promoHtml(list, index);
    if (!featured) {
        return `<section class="hero is-compact"><div class="hero-content"><h1 class="hero-title">${t('home.welcome')}</h1></div></section>`;
    }
    const it = featured.item;
    const d = heroData(featured);
    const many = list.length > 1;
    // La imagen va en un <img> (no como fondo CSS): si el fondo del proveedor falla se prueba
    // con la carátula en lugar de dejar el destacado en negro.
    return `<section class="hero ${many ? 'has-carousel' : ''} ${lib ? 'is-lib' : ''}" data-featured="${featured.kind}:${it.id}">
        <div class="hero-bg hero-fade">${d.image ? `<img class="hero-img" fetchpriority="high" decoding="async" src="${escapeHtml(imageUrl(d.image))}" data-fallback="${escapeHtml(d.fallback && d.fallback !== d.image ? imageUrl(d.fallback) : '')}" alt="" decoding="async"/>` : ''}</div>
        <div class="hero-content hero-fade">
            <div class="hero-kicker"><span class="mi">${featured.kind === 'vod' ? 'movie' : 'tv'}</span>${featured.isNew ? t('home.newAdded') : t('home.featured')} · ${featured.kind === 'vod' ? t('nav.movies') : t('nav.series')}</div>
            <h1 class="hero-title">${escapeHtml(stripCountry(d.name))}</h1>
            <div class="hero-meta" data-hero-meta>${heroMetaHtml(d)}</div>
            ${d.plot ? `<p class="hero-desc">${escapeHtml(d.plot)}</p>` : ''}
            <div class="hero-actions">
                <button class="btn btn-primary btn-lg" data-hero-play="${featured.kind}:${it.id}"${navDefault ? ' data-nav-default' : ''}><span class="mi">play_arrow</span>${t('common.play')}</button>
                <button class="btn btn-lg" data-open="${featured.kind}:${it.id}"><span class="mi">info</span>${t('common.info')}</button>
            </div>
        </div>
        ${many ? `<button class="hero-arrow is-left" data-hero-step="-1" aria-label="${t('hero.prev')}" data-nav-skip><span class="mi">chevron_left</span></button>
        <button class="hero-arrow is-right" data-hero-step="1" aria-label="${t('hero.next')}" data-nav-skip><span class="mi">chevron_right</span></button>
        <div class="hero-dots" data-nav-row>${list.map((x, i) => `<button class="hero-dot ${i === index ? 'is-active' : ''}" data-hero-go="${i}" aria-label="${escapeHtml(stripCountry(x.item.name))}"></button>`).join('')}</div>` : ''}
    </section>`;
}

const today = () => new Date().toISOString().slice(0, 10);

/** «caduca hoy / mañana / en 3 días» */
export function whenText(daysLeft) {
    return daysLeft <= 0 ? t('sub.today') : daysLeft === 1 ? t('sub.tomorrow') : t('sub.inDays', { n: daysLeft });
}

/** Aviso de suscripción caducada o a punto de caducar del proveedor en uso. */
function noticeHtml() {
    if (!catalog.profile) return '';
    const sub = catalog.subscription();
    const name = catalog.profile.name;
    const date = sub.expiresAt ? formatDate(sub.expiresAt, i18n.lang) : '';
    const sim = sub.simulated ? `<span class="sub-notice-sim">${t('sub.simulated')}</span>` : '';
    const actions = `<button class="btn btn-sm" data-sub-recheck><span class="mi">refresh</span>${t('sub.recheck')}</button><a class="btn btn-sm" href="#" data-href="/settings/?tab=sources"><span class="mi">dns</span>${t('sub.manage')}</a>`;
    if (sub.state === 'expired' || sub.state === 'disabled') {
        const title = sub.state === 'disabled' ? t('sub.noticeDisabledTitle', { name }) : t('sub.noticeExpiredTitle', { name });
        const backup = catalog.sources.size ? ' ' + t('sub.noticeBackup') : '';
        const text = (sub.state === 'disabled' ? t('sub.noticeDisabledText') : t(date ? 'sub.noticeExpiredText' : 'sub.noticeExpiredTextNoDate', { date })) + backup;
        return `<section class="sub-notice is-expired" role="alert"><span class="mi">event_busy</span><div class="sub-notice-body"><b>${escapeHtml(title)}</b><span>${escapeHtml(text)} ${sim}</span><div class="sub-notice-actions" data-nav-row>${actions}</div></div></section>`;
    }
    if (sub.state === 'expiring' && storage.get('subNoticeHidden', '') !== today()) {
        return `<section class="sub-notice is-soon"><span class="mi">schedule</span><div class="sub-notice-body"><b>${escapeHtml(t('sub.noticeSoonTitle', { name, when: whenText(sub.daysLeft) }))}</b><span>${escapeHtml(t('sub.noticeSoonText', { date }))} ${sim}</span><div class="sub-notice-actions" data-nav-row>${actions}</div></div><button class="btn btn-icon sub-notice-close" data-sub-dismiss aria-label="${t('common.close')}"><span class="mi">close</span></button></section>`;
    }
    return '';
}

// Filas que se pintan con la propia página; las demás se añaden una por fotograma, para que el Inicio responda al
// instante aunque el catálogo sea grande (con 9 filas y 200 tarjetas de golpe, un móvil medio se quedaba 1 s clavado).
const FIRST_ROWS = 2;
let initialParts = null; // filas calculadas en render() que mount() termina de colocar

function rowsHtml() {
    return rowParts().join('');
}

// Listas ordenadas que pide el Inicio: ordenar más de 10 000 títulos en cada repintado (aviso de la guía, progreso…) costaba
// más que pintar. Se recuerdan mientras no cambie el catálogo visible.
const sortedMemo = { vod: null, series: null, sig: '', out: null };
/**
 * Los `k` primeros según `cmp` sin ordenar la lista entera: con 60 000 títulos, ordenar todo para quedarse con 24 era lo más caro
 * del Inicio. Mismo resultado que `list.slice().sort(cmp).slice(0, k)` (los empates conservan el orden de llegada).
 */
export function topK(list, k, cmp) {
    const out = [];
    if (!(k > 0)) return out;
    for (let i = 0; i < list.length; i++) {
        const x = list[i];
        if (out.length === k && !(cmp(x, out[k - 1]) < 0)) continue;
        let j = out.length;
        while (j > 0 && cmp(x, out[j - 1]) < 0) j--;
        out.splice(j, 0, x);
        if (out.length > k) out.pop();
    }
    return out;
}

function sortedLists(hf) {
    const sig = hf.signature;
    const vod = catalog.visibleRef('vod');
    const series = catalog.visibleRef('series');
    if (sortedMemo.out && sortedMemo.vod === vod && sortedMemo.series === series && sortedMemo.sig === sig) return sortedMemo.out;
    const vodAll = hf.list('vod', vod);
    const seriesAll = hf.list('series', series);
    // Sin fecha de alta (o sin nota) cuenta como 0: con «undefined» el comparador daba NaN y el orden era arbitrario.
    const byAdded = (a, b) => (b.added || 0) - (a.added || 0);
    const out = {
        recentVod: topK(vodAll, 24, byAdded),
        recentSeries: topK(seriesAll, 24, byAdded),
        topVod: topK(vodAll.filter((m) => m.rating >= 7), 24, (a, b) => (b.rating || 0) - (a.rating || 0)),
        added: topK(vodAll.filter((x) => x.added > 0).map((item) => ({ kind: 'vod', item }))
            .concat(seriesAll.filter((x) => x.added > 0).map((item) => ({ kind: 'series', item }))), 24, (a, b) => (b.item.added || 0) - (a.item.added || 0))
    };
    Object.assign(sortedMemo, { vod, series, sig, out });
    return out;
}

function rowParts() {
    const parts = [];
    // Lo que la persona filtró en TV en directo, Películas y Series (proveedor, país, categorías, calidad) vale también aquí.
    const hf = homeFilter();
    const cont = hf.entries(catalog.continueWatching(hf.on ? 80 : 20)).slice(0, 20);
    if (cont.length) parts.push(row({ id: 'continue', title: t('home.continue'), items: cont.map(continueCard), seeAll: '/mylist/', kind: 'wide' }));
    // Los directos son otra fila: los canales vistos hace poco (no se «continúan» como una película, se vuelven a sintonizar).
    if (settings.get('continueChannels') !== false) {
        const live = hf.list('live', catalog.recentChannels()).slice(0, 12);
        if (live.length) parts.push(row({ id: 'continue-live', title: t('home.continueLive'), items: live.map((ch) => channelCard(ch, { wide: true })), seeAll: '/live/', kind: 'wide' }));
    }

    // «Top 10»: lo más visto de la semana (contador propio del servidor o tendencias de TMDB, según Ajustes) que hay en este catálogo.
    const topSource = settings.get('topSource') || 'own';
    const top = topSource === 'off' ? { source: 'off', entries: [] } : catalog.topTen(hf.on ? 40 : 10, topSource);
    const topEntries = hf.entries(top.entries).slice(0, 10);
    if (topEntries.length >= TOP_MIN) {
        parts.push(row({
            id: 'top10', kind: 'top',
            title: t(top.source === 'own' ? 'home.top10' : 'home.top10Trending'),
            items: topEntries.map((r, i) => topItem(i + 1, r.kind === 'vod' ? movieCard(r.item) : seriesCard(r.item)))
        }));
    }

    // Lo nuevo en Jellyfin, Emby y Plex desde la última vez (con un botón para darlo por visto).
    const news = hf.entries(catalog.libraryNews(hf.on ? 120 : 24)).slice(0, 24);
    if (news.length) parts.push(row({ id: 'libnews', title: t('home.libraryNews'), items: news.map((r) => (r.kind === 'vod' ? movieCard(r.item) : seriesCard(r.item))), seeAllAction: '' }).replace('</h2>', `</h2><a class="section-link" href="#" data-news-seen>${escapeHtml(t('home.libraryNewsSeen'))} <span class="mi">done_all</span></a>`));

    // Lo último que ha entrado en el catálogo (películas y series juntas), arriba como en Plex. Solo si los proveedores traen fecha de alta.
    const sorted = sortedLists(hf);
    const addedRow = sorted.added;
    const hasAdded = addedRow.length >= 6;
    if (hasAdded) parts.push(row({ id: 'added', title: t('home.recentlyAdded'), items: addedRow.map((r) => (r.kind === 'vod' ? movieCard(r.item) : seriesCard(r.item))), seeAll: '/movies/' }));

    // Solo con historial real del espectador y suficientes títulos afines; si no, la fila no sale.
    const taste = catalog.tasteRows(24, 2);
    const rec = hf.entries(taste.recommended);
    if (rec.length >= RECOMMENDED_MIN) parts.push(row({ id: 'recommended', title: t('home.recommended'), items: rec.map((r) => (r.kind === 'vod' ? movieCard(r.item) : seriesCard(r.item))) }));

    // «Porque viste X»: lo más parecido a lo último que se vio entero (director, reparto, géneros, saga). Hasta dos filas.
    taste.because.forEach((r) => parts.push(row({
        id: 'because-' + r.kind + '-' + r.seed.id,
        title: t('home.because', { title: stripCountry(r.seed.name) }),
        items: hf.entries(r.items).map((x) => (x.kind === 'vod' ? movieCard(x.item) : seriesCard(x.item)))
    })));

    // Tendencias reales de la semana (TMDB), solo con lo que hay en el catálogo.
    // Si el Top 10 ya se alimenta de las tendencias, no se repite la misma fila.
    const trend = topSource === 'trending' && topEntries.length >= TOP_MIN ? [] : hf.entries(catalog.trending(hf.on ? 80 : 20)).slice(0, 20);
    if (trend.length >= TRENDING_MIN) parts.push(row({ id: 'trending', title: t('home.trending'), items: trend.map((r) => (r.kind === 'vod' ? movieCard(r.item) : seriesCard(r.item))) }));

    // Colecciones de los servidores propios (sagas, ciclos…).
    const colls = catalog.collections();
    if (colls.length) parts.push(row({ id: 'collections', title: t('home.collections'), items: colls.slice(0, 24).map(collectionCard) }));

    const favLive = catalog.favoriteItems('live');
    const favVod = catalog.favoriteItems('vod');
    const favSeries = catalog.favoriteItems('series');
    const myList = favVod.map((m) => movieCard(m)).concat(favSeries.map((m) => seriesCard(m)));
    if (myList.length) parts.push(row({ id: 'mylist', title: t('home.mylist'), items: myList, seeAll: '/mylist/' }));

    const recentLive = hf.list('live', catalog.recentChannels());
    const liveNow = favLive.length ? favLive : recentLive.length ? recentLive : hf.list('live', catalog.visible('live')).slice(0, 20);
    if (liveNow.length) parts.push(row({ id: 'live', title: favLive.length ? t('live.favorites') : recentLive.length ? t('home.recentChannels') : t('home.liveNow'), items: liveNow.slice(0, 24).map(channelCard), seeAll: '/live/', kind: 'channel' }));

    if (sorted.recentVod.length) {
        if (!hasAdded) parts.push(row({ id: 'newmovies', title: t('home.newMovies'), items: sorted.recentVod.map((m) => movieCard(m)), seeAll: '/movies/' }));
        if (sorted.topVod.length >= 6) parts.push(row({ id: 'topmovies', title: t('home.topRated'), items: sorted.topVod.map((m) => movieCard(m)) }));
    }

    if (sorted.recentSeries.length && !hasAdded) parts.push(row({ id: 'newseries', title: t('home.newSeries'), items: sorted.recentSeries.map((m) => seriesCard(m)), seeAll: '/series/' }));

    // Algunas categorías de películas/series como filas temáticas.
    // Con categorías elegidas en Películas/Series salen esas, no las primeras.
    const wantV = hf.cats('vod');
    const cats = wantV.length ? catalog.categories.vod.filter((c) => !c.adult && wantV.indexOf(String(c.id)) !== -1).slice(0, 4) : catalog.categories.vod.filter((c) => !c.adult && c.count >= 8).slice(0, 4);
    cats.forEach((c) => {
        const items = hf.list('vod', catalog.byCategory('vod', c.id)).slice(0, 24);
        parts.push(row({ id: 'cat-' + c.id, title: c.name, items: items.map((m) => movieCard(m)), seeAll: '/movies/?cat=' + encodeURIComponent(c.id) }));
    });
    const wantS = hf.cats('series');
    const scats = wantS.length ? catalog.categories.series.filter((c) => !c.adult && wantS.indexOf(String(c.id)) !== -1).slice(0, 2) : catalog.categories.series.filter((c) => !c.adult && c.count >= 6).slice(0, 2);
    scats.forEach((c) => {
        const items = hf.list('series', catalog.byCategory('series', c.id)).slice(0, 24);
        parts.push(row({ id: 'scat-' + c.id, title: c.name, items: items.map((m) => seriesCard(m)), seeAll: '/series/?cat=' + encodeURIComponent(c.id) }));
    });

    if (!parts.length) {
        return [empty({ icon: 'subscriptions', title: t('home.emptyTitle'), text: t('home.emptyText') })];
    }
    return parts;
}

export default {
    path: '/',
    alias: ['/home/'],
    nav: 'home',

    render() {
        return `<div class="page page-home" data-name="home">
            <div class="page-content">
                <div class="home-topbar">
                    <span class="brand"><img src="img/logo.png" alt=""/>${t('app.name')}</span>
                    <span class="home-topbar-actions">
                        <a class="btn btn-icon" href="#" data-href="/search/" aria-label="${t('nav.search')}"><span class="mi">search</span></a>
                        <a class="home-topbar-avatar" href="#" data-menu aria-haspopup="true" aria-label="${t('nav.menu')}">${avatarHtml(viewers.current(), 'is-small')}</a>
                    </span>
                </div>
                <div data-hero></div>
                <div data-notice></div>
                <div data-rows>${(initialParts = catalog.loaded ? rowParts() : null) ? initialParts.slice(0, FIRST_ROWS).join('') : skeletonRow('wide') + skeletonRow('channel') + skeletonRow()}</div>
            </div>
        </div>`;
    },

    mount(page) {
        const root = page.el;
        bindExpandCards(root); // tarjeta que crece dentro de la fila (ajuste «Tarjeta ampliada»), igual que en Películas y Series
        const heroEl = root.querySelector('[data-hero]');
        // Con cabecera destacada, la imagen llega hasta arriba y el menú va transparente encima (como en Prime Video): la página lo marca y main.js lo aplica al menú.
        const markHeroTop = () => {
            const on = !!heroEl.querySelector('.hero:not(.is-compact)');
            if (root.classList.contains('has-hero-top') === on) return;
            root.classList.toggle('has-hero-top', on);
            root.dispatchEvent(new Event('hero-top'));
        };
        if (typeof MutationObserver !== 'undefined') new MutationObserver(markHeroTop).observe(heroEl, { childList: true });
        const rowsEl = root.querySelector('[data-rows]');
        const noticeEl = root.querySelector('[data-notice]');
        let lastNotice = '';
        const paintNotice = () => {
            const html = noticeHtml();
            if (html === lastNotice) return;
            lastNotice = html;
            noticeEl.innerHTML = html;
        };
        let featured = [];
        let heroIndex = 0;
        let heroTimer = null;
        let lastRows = '';
        const rowHtmlById = new Map(); // texto generado de cada fila la última vez que se pintó
        const rowIdOf = (part) => { const m = /data-row-id="([^"]*)"/.exec(part); return m ? m[1] : ''; };
        // Resto de filas del primer pintado, una por fotograma. Mientras tanto paint() no toca las filas.
        let deferred = null;
        let repaintAfter = false;
        const stopDeferred = () => { if (deferred) { cancelAnimationFrame(deferred.raf); deferred = null; } };
        if (initialParts) {
            const parts = initialParts;
            initialParts = null;
            const full = parts.join('');
            parts.forEach((part) => rowHtmlById.set(rowIdOf(part), part));
            if (parts.length <= FIRST_ROWS) lastRows = full;
            else {
                deferred = { parts, i: FIRST_ROWS, raf: 0 };
                const step = () => {
                    if (!deferred) return;
                    rowsEl.insertAdjacentHTML('beforeend', deferred.parts[deferred.i++]);
                    if (rowsEl.lastElementChild) enhance(rowsEl.lastElementChild);
                    if (deferred.i < deferred.parts.length) { deferred.raf = requestAnimationFrame(step); return; }
                    deferred = null;
                    lastRows = full;
                    if (repaintAfter) { repaintAfter = false; paint(); }
                };
                deferred.raf = requestAnimationFrame(step);
            }
        }

        const heroValid = () => {
            if (!featured.length) return false;
            const real = featured.filter((f) => f.kind !== 'promo');
            // Con destacados reales, que sigan en el catálogo; y que haya (o no) destacados lo mismo que al montar la lista.
            return real.every((f) => catalog.get(f.kind, f.item.id)) && (real.length > 0) === hasFeaturable();
        };
        let alive = true;
        const bindHeroImage = () => {
            const im = heroEl.querySelector('.hero-img');
            if (!im) return;
            im.addEventListener('error', () => {
                const alt = im.getAttribute('data-fallback');
                im.removeAttribute('data-fallback');
                if (alt) im.src = alt; else im.remove();
            });
        };
        const renderHero = () => {
            // En la tele el foco se conserva al repintar la cabecera (si no, saltaba a otro sitio y la página se desplazaba).
            if (device.isTV && heroEl.contains(document.activeElement)) window.IPTV.tvnav.preserve(() => { heroEl.innerHTML = heroHtml(featured, heroIndex); }, heroEl);
            else heroEl.innerHTML = heroHtml(featured, heroIndex);
            bindHeroImage();
            // Precarga el fondo del siguiente para que el cambio sea instantáneo.
            const next = featured[(heroIndex + 1) % featured.length];
            if (next && next.kind !== 'promo') { const src = heroData(next).image; if (src) { const im = new Image(); im.src = imageUrl(src); } }
        };
        // La lista del proveedor casi nunca trae fondo ni duración: se piden las fichas de los
        // destacados (cinco, con caché) y se completa el que está a la vista sin repintarlo entero.
        const loadHeroDetails = (list) => {
            list.filter((f) => f.kind !== 'promo').forEach((f) => {
                const job = f.kind === 'vod' ? catalog.vodInfo(f.item.id) : catalog.seriesInfo(f.item.id);
                Promise.resolve(job).then((info) => {
                    if (!alive || !info || featured.indexOf(f) === -1) return;
                    const before = heroData(f);
                    f.info = info;
                    if (featured[heroIndex] !== f) return;
                    const after = heroData(f);
                    const meta = heroEl.querySelector('[data-hero-meta]');
                    if (meta) meta.innerHTML = heroMetaHtml(after);
                    if (after.image !== before.image || (after.plot && !before.plot)) renderHero();
                }).catch(() => {});
            });
        };
        const goHero = (i, { manual = false } = {}) => {
            if (!featured.length) return;
            heroIndex = ((i % featured.length) + featured.length) % featured.length;
            renderHero();
            enhance(heroEl);
            if (manual) scheduleHero();
        };
        // Rotación automática: se detiene con el ratón encima, con el foco del mando dentro, en
        // segundo plano o cuando el reproductor está abierto.
        const scheduleHero = () => {
            clearInterval(heroTimer);
            const every = heroIntervalMs();
            if (featured.length < 2 || !every) return;
            heroTimer = setInterval(() => {
                if (document.hidden || (window.IPTV.player && window.IPTV.player.isOpen)) return;
                const hero = heroEl.querySelector('.hero');
                if (!hero || hero.matches(':hover') || hero.querySelector('.is-focused')) return;
                goHero(heroIndex + 1);
            }, every);
        };

        const paint = () => {
            paintNotice();
            if (!catalog.loaded) {
                if (catalog.error && !catalog.loading) {
                    heroEl.innerHTML = '';
                    rowsEl.innerHTML = empty({
                        icon: 'cloud_off',
                        title: t('common.errorLoad'),
                        text: t('common.errorNetwork'),
                        action: `<div class="flex"><button class="btn btn-primary" data-retry-catalog data-nav-default><span class="mi">refresh</span>${t('common.retry')}</button>${window.IPTV.offline.hasDone() ? `<a class="btn" href="#" data-href="/offline/"><span class="mi">download</span>${t('offline.seeAll')}</a>` : ''}<a class="btn" href="#" data-href="/settings/"><span class="mi">settings</span>${t('nav.settings')}</a></div>`
                    });
                    lastRows = '';
                }
                return;
            }
            if (!heroValid()) {
                featured = buildHeroList();
                heroIndex = 0;
                scheduleHero();
                loadHeroDetails(featured);
            }
            // Repintar solo si cambia el destacado: así no se reinicia la imagen con cada aviso del catálogo.
            const shown = heroEl.querySelector('[data-featured]');
            const current = featured[heroIndex];
            if (!shown || !current || shown.getAttribute('data-featured') !== current.kind + ':' + current.item.id) renderHero();
            if (deferred) { repaintAfter = true; enhance(heroEl); return; }
            const parts = rowParts();
            const html = parts.join('');
            if (html !== lastRows) {
                // Reemplazar solo las filas cuyo texto cambia (comparando con el generado la última vez, no serializando el DOM)
                // para no perder el scroll horizontal ni volver a crear tarjetas que siguen iguales.
                const nextIds = parts.map(rowIdOf);
                Array.from(rowsEl.children).forEach((el) => {
                    const id = el.getAttribute('data-row-id') || '';
                    if (nextIds.indexOf(id) === -1) { el.remove(); rowHtmlById.delete(id); }
                });
                parts.forEach((part, i) => {
                    const id = nextIds[i];
                    let el = id ? rowsEl.querySelector(`[data-row-id="${id}"]`) : rowsEl.querySelector(':scope > :not([data-row-id])');
                    if (!el || rowHtmlById.get(id) !== part) {
                        const tmp = document.createElement('div');
                        tmp.innerHTML = part;
                        const fresh = tmp.firstElementChild;
                        if (!fresh) return;
                        if (el) {
                            const scroller = el.querySelector('.row-scroller');
                            const left = scroller ? scroller.scrollLeft : 0;
                            el.replaceWith(fresh);
                            const ns = fresh.querySelector('.row-scroller');
                            if (ns && left) ns.scrollLeft = left;
                        }
                        el = fresh;
                        rowHtmlById.set(id, part);
                    }
                    if (rowsEl.children[i] !== el) rowsEl.insertBefore(el, rowsEl.children[i] || null);
                });
                lastRows = html;
            }
            enhance(root);
            // Solo se devuelve el foco a la página si no lo tiene nadie (se repintó el elemento enfocado). Si está en el menú o en otro
            // sitio, no se le quita: antes cada repintado (catálogo, guía) lo mandaba al botón «Reproducir» en mitad de la navegación.
            if (device.isTV && !document.querySelector('.is-focused')) {
                setTimeout(() => window.IPTV.tvnav.focusLayer(root), 60);
            }
        };

        const heroPlay = (e) => {
            if (e.target.closest('[data-sub-dismiss]')) {
                e.preventDefault();
                storage.set('subNoticeHidden', today());
                paintNotice();
                return;
            }
            const recheck = e.target.closest('[data-sub-recheck]');
            if (recheck) {
                e.preventDefault();
                recheck.disabled = true;
                catalog.checkSubscription().catch(() => catalog.subscription()).then((sub) => {
                    recheck.disabled = false;
                    if (sub.state === 'expired' || sub.state === 'disabled') notify.error(t('sub.stillExpired'));
                    else notify.toast(t('sub.nowActive'), { icon: 'check_circle' });
                    paintNotice();
                });
                return;
            }
            const step = e.target.closest('[data-hero-step]');
            if (step) { e.preventDefault(); goHero(heroIndex + Number(step.getAttribute('data-hero-step')), { manual: true }); return; }
            const go = e.target.closest('[data-hero-go]');
            if (go) { e.preventDefault(); goHero(Number(go.getAttribute('data-hero-go')), { manual: true }); return; }
            const btn = e.target.closest('[data-hero-play]');
            if (!btn) return;
            e.preventDefault();
            const [kind, id] = btn.getAttribute('data-hero-play').split(':');
            window.IPTV.actions.playFromCatalog(kind, id);
        };
        root.addEventListener('click', heroPlay);
        // Deslizar en táctil para cambiar de destacado. Se usan eventos touch (no pointer) porque
        // en cuanto el navegador decide desplazar la página cancela el puntero y nunca llega pointerup.
        // Solo cuenta si el gesto es claramente horizontal, para no robar el desplazamiento vertical.
        let swipe = null;
        heroEl.addEventListener('touchstart', (e) => {
            const tch = e.touches[0];
            swipe = tch && e.touches.length === 1 ? { x: tch.clientX, y: tch.clientY, t: Date.now() } : null;
        }, { passive: true });
        heroEl.addEventListener('touchend', (e) => {
            if (!swipe) return;
            const tch = e.changedTouches[0];
            const dx = tch.clientX - swipe.x;
            const dy = tch.clientY - swipe.y;
            const fast = Date.now() - swipe.t < 800;
            swipe = null;
            if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5 && fast) goHero(heroIndex + (dx < 0 ? 1 : -1), { manual: true });
        }, { passive: true });
        heroEl.addEventListener('touchcancel', () => { swipe = null; }, { passive: true });

        paint();
        // Las pantallas de presentación las puede cambiar el servidor: se piden en segundo plano y, si hay novedades, se repinta.
        {
            refreshSlides().then((fresh) => {
                if (!alive || !fresh || !catalog.loaded) return;
                featured = buildHeroList();
                heroIndex = 0;
                renderHero();
                enhance(heroEl);
                scheduleHero();
            }).catch(() => {});
        }
        catalog.loadTrending();
        catalog.loadTop();
        if (catalog.loaded) catalog.checkSubscriptionSoon();
        const off = [
            catalog.on('change', debounce(paint, 150)),
            epg.on('change', debounce(paint, 300))
        ];
        if (device.isTV) setTimeout(() => window.IPTV.tvnav.focusLayer(root), 100);
        return () => {
            alive = false;
            stopDeferred();
            clearInterval(heroTimer);
            off.forEach((fn) => fn());
        };
    }
};
